import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ACTIONS from "../../../../redux/action";
import axios from "../../../../shared/axios/axiosProxy";
import i18n from "../../../../utils/i18n";
import RevenueSlider from "../../components/revenueSlider/RevenueSlider.jsx";
import classes from "./companyRevenue.module.scss";

const COMPANY_REVENUE_STEPS = [
  { data: 5, display: "0 - 5" },
  { data: 10, display: "5 - 12" },
  { data: 50, display: "12 - 50" },
  { data: 100, display: "50 - 100" },
  { data: 200, display: "100 - 200" },
  { data: 500, display: "200 - 500" },
  { data: 1999, display: "500 - 2000" },
  { data: 2000, display: "2000+" },
];

/** First revenue bucket (0–5 M Ft); used when the step loads without a prior answer. */
const DEFAULT_REVENUE = COMPANY_REVENUE_STEPS[0].data;
const REVENUE_IDK = -10;

// Slider index whose range best matches the stored revenue.
// Returns 0 while nothing has been selected yet (yearlyrevenue <= 0).
const getSliderIndexForRevenue = (steps, yearlyrevenue) => {
  if (!steps.length || yearlyrevenue <= 0) {
    return 0;
  }

  let nearestIndex = 0;
  let smallestDistance = Infinity;
  steps.forEach((step, index) => {
    const distance = Math.abs(step.data - yearlyrevenue);
    if (distance < smallestDistance) {
      smallestDistance = distance;
      nearestIndex = index;
    }
  });
  return nearestIndex;
};

export const isCompanyRevenueAnswered = (yearlyrevenue) =>
  yearlyrevenue > 0 || yearlyrevenue === REVENUE_IDK;

const CompanyRevenue = () => {
  const dispatch = useDispatch();
  const { yearlyrevenue, yearlyrevenueSource, searchID } = useSelector(
    (state) => state.company_store,
  );

  const steps = COMPANY_REVENUE_STEPS;
  const sliderMarks = useMemo(
    () => steps.map((_, index) => ({ value: index })),
    [steps],
  );

  const [sliderIndex, setSliderIndex] = useState(() =>
    getSliderIndexForRevenue(steps, yearlyrevenue),
  );

  const currentStep = steps[sliderIndex];
  const displayRange = currentStep?.display ?? "";
  const isRevenueIdk = yearlyrevenue === REVENUE_IDK;

  const persistRevenue = useCallback(
    (value, source = "user") => {
      dispatch(
        ACTIONS.addCompanyInfo({
          id: "yearlyrevenue",
          data: value,
          stateObject: "company_store",
        }),
      );
      dispatch(
        ACTIONS.addCompanyInfo({
          id: "yearlyrevenueSource",
          data: source,
          stateObject: "company_store",
        }),
      );
      if (!searchID) {
        return;
      }
      axios
        .patch(
          `/api/search/${searchID}`,
          { yearlyrevenue: value },
          { headers: { "Content-Type": "application/json" } },
        )
        .catch((error) => {
          console.error(
            "Error saving yearly revenue to searchSchema:",
            error.response?.data || error.message,
          );
        });
    },
    [dispatch, searchID],
  );

  // Persist a revenue value to redux + searchSchema and move the slider to it.
  // `source` records whether this write is a system default or a real user pick.
  const saveRevenue = useCallback(
    (value, index, source = "user") => {
      if (value !== REVENUE_IDK) {
        setSliderIndex(index);
      }
      persistRevenue(value, source);
    },
    [persistRevenue],
  );

  // Sync slider with store: restore a saved value; on <= 0 reset slider to first bucket,
  // default to 5 M Ft once on first visit, and normalize legacy negative values.
  const didInitializeRef = useRef(false);
  useEffect(() => {
    if (yearlyrevenue === REVENUE_IDK) {
      return;
    }

    if (yearlyrevenue > 0) {
      const index = getSliderIndexForRevenue(steps, yearlyrevenue);
      const step = steps[index];
      if (step && step.data !== yearlyrevenue) {
        // Rounding an existing value to its nearest bucket isn't a new user
        // choice — keep whatever source tag the value already had.
        saveRevenue(step.data, index, yearlyrevenueSource);
      } else {
        setSliderIndex(index);
      }
      return;
    }

    setSliderIndex(0);

    if (!didInitializeRef.current) {
      didInitializeRef.current = true;
      saveRevenue(DEFAULT_REVENUE, 0, "default");
      return;
    }

    if (yearlyrevenue < 0) {
      saveRevenue(DEFAULT_REVENUE, 0, "default");
    }
  }, [yearlyrevenue, yearlyrevenueSource, steps, saveRevenue]);

  const handleIdkChange = useCallback(
    (event) => {
      if (event.target.checked) {
        persistRevenue(REVENUE_IDK);
        return;
      }
      saveRevenue(DEFAULT_REVENUE, 0);
    },
    [persistRevenue, saveRevenue],
  );

  const commitIndex = useCallback(
    (index) => {
      const step = steps[index];
      if (step) {
        saveRevenue(step.data, index);
      }
    },
    [steps, saveRevenue],
  );

  // Live drag only updates the local index; the value is persisted on release.
  const handleSliderChange = useCallback((_, value) => {
    setSliderIndex(Number(value));
  }, []);

  const handleSliderCommit = useCallback(
    (_, value) => commitIndex(Number(value)),
    [commitIndex],
  );

  const handleStepChange = useCallback(
    (delta) => {
      const nextIndex = Math.min(
        steps.length - 1,
        Math.max(0, sliderIndex + delta),
      );
      commitIndex(nextIndex);
    },
    [commitIndex, sliderIndex, steps.length],
  );

  return (
    <div className={classes.root}>
      <div className={classes.scrollArea}>
        <div className={classes.content}>
          <div className={classes.mainColumn}>
            <header className={classes.header}>
              <h1 className={classes.title}>
                {i18n.t("searchflow.revenuetitle")}
              </h1>
              <p className={classes.subtitle}>
                {i18n.t("searchflow.revenuesubtitle")}
              </p>
            </header>

            <div className={classes.rightColumn}>
              <div className={classes.body}>
                <div
                  className={`${classes.controls} ${isRevenueIdk ? classes.controlsDisabled : ""}`}
                >
                  <div className={classes.valueRow}>
                    <button
                      type="button"
                      className={classes.stepButton}
                      aria-label={i18n.t("searchflow.revenueprevaria")}
                      onClick={() => handleStepChange(-1)}
                      disabled={isRevenueIdk || sliderIndex <= 0}
                    >
                      −
                    </button>

                    <div className={classes.valueDisplay}>
                      <div className={classes.valueRange}>{displayRange}</div>
                      <div className={classes.valueUnit}>{i18n.t("searchflow.millionforint")}</div>
                    </div>

                    <button
                      type="button"
                      className={classes.stepButton}
                      aria-label={i18n.t("searchflow.revenuenextaria")}
                      onClick={() => handleStepChange(1)}
                      disabled={isRevenueIdk || sliderIndex >= steps.length - 1}
                    >
                      +
                    </button>
                  </div>

                  <RevenueSlider
                    value={sliderIndex}
                    max={Math.max(0, steps.length - 1)}
                    marks={sliderMarks}
                    disabled={isRevenueIdk}
                    onChange={handleSliderChange}
                    onChangeCommitted={handleSliderCommit}
                  />
                </div>

                <label className={classes.checkboxRow}>
                  <input
                    type="checkbox"
                    className={classes.checkboxInput}
                    checked={isRevenueIdk}
                    onChange={handleIdkChange}
                    aria-label={i18n.t("searchflow.revenueidk")}
                  />
                  <span
                    className={`${classes.checkbox} ${
                      isRevenueIdk ? classes.checkboxChecked : ""
                    }`}
                    aria-hidden="true"
                  />
                  <span className={classes.checkboxLabel}>
                    {i18n.t("searchflow.revenueidk")}
                  </span>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyRevenue;
